import React, { useImperativeHandle, useMemo, useRef, forwardRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import Modal from "src/components/Modal";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { ActionGroupers } from "src/pages/Actions/Panel/interface";
import ListOfActions, { IRefListOfActions } from "src/pages/Actions/Panel/listOfActions";
import { standardErrorMessages } from "src/utils/errors";
import { IRefModalHistoric, IRisks } from "../interface";
import ListAnalysis from "./ListAnalysis";
import ListIncidents from "./ListIncidents";

export interface IComponentModalHistoric{
    onCallback?: Function;
}

const ModalHistoric: React.ForwardRefRenderFunction<IRefModalHistoric, IComponentModalHistoric> = ({
	onCallback
}, ref) => {
	const [selectedRisk, setSelectedRisk] = useState<IRisks>();

	const { setLoading } = useLoaderBackdrop();

	const { addToast } = RCCHookToast();

	const listOfActionsRef = useRef<IRefListOfActions>(null);
	const modalHistoric = useRef<RCCRefModalProps>(null);
	const { translate } = useTranslation();

	const onCallbackAnalysis = () => {
		loadActions();
		
		if(onCallback)
			onCallback(selectedRisk?.rib001Id);
	};


	const ListOfAnalysis = useMemo(() => (
		<>
			{selectedRisk && (
				<ListAnalysis 
					riskData={selectedRisk}
					onCallback={onCallbackAnalysis}
				/>
			)}
		</>
	), [selectedRisk?.ritbb003]);

	const ListOfIncidents = useMemo(() => (
		<>
			{selectedRisk && (
				<ListIncidents 
					data={selectedRisk.ritbb004}
				/>
			)}
		</>
	), [selectedRisk?.ritbb004]);

	useImperativeHandle(ref, () => ({
		actionButtonHistoric
	}));

	const actionButtonHistoric = (row: IRisks) => {
		setLoading(true);
		getSelectedRisk(row.rib001Id);
	};

	const getSelectedRisk = async (id: number) => {
		try{
			const risk = await RCCServicesApi.getRequest({
				method: "Risks/Select",
				urlParameters: {
					id
				}
			}); 
			setSelectedRisk(risk.data);
			setLoading(false);
			modalHistoric.current?.openModal();				
			loadActions();
			
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar o risco"), addToast);
			setLoading(false);
		}
	};

	const loadActions = () => {
		listOfActionsRef.current?.executeQuery();
	};
  
	return (
		<>
			{selectedRisk && (
				<>
					<Modal	
						ref={modalHistoric}
						header={{
							title: translate("Histórico de risco"),
							icon: {
								name: "history"
							},
							subtitle:  `${translate("Risco")}: ${selectedRisk.rib001Titulo}`
						}}
						withoutForm
						footer={{
							showSaveButton: false,
							cancelButtonText: translate("Cancelar")
						}}
					>

						<Tabs 
							name="tabHistoric"
							tabInit={0}
							options={[
								{
									label: translate("Ações"),
									content: (
										<>
											<ListOfActions 
												ref={listOfActionsRef}
												header={{showTotal: true}}
												layoutList="card"
												positionOptions="bottomEnd"
												groupers={{
													grouperId: selectedRisk.rib001Id 
												}}
												groupersSelect={{
													group: ActionGroupers.RISK,
													delayed: "",
													origin: ""
												}}
											/>
										</>
									)
								}, 
								{
									label: translate("Análises"),
									content: (
										<>
											<RCCGrid
												container
												item
												justifyContent="space-between"
											>
												<Title color="purple" size="md">
													{translate("Total")}: {selectedRisk.ritbb003 ? (
														<>{selectedRisk.ritbb003.length} {selectedRisk.ritbb003.length > 1 ? translate("análises") : translate("análise")}</>
													): (
														<>{`0 ${translate("análises")}`}</>
													)} 
												</Title>
											</RCCGrid>
											<hr />

											{ListOfAnalysis}
											
										</>
									)
								},
								{
									label: translate("Incidentes"),
									content: (
										<>
											<RCCGrid
												container
												item
												justifyContent="space-between"
											>
												<Title color="purple" size="md">
													{translate("Total")}: {selectedRisk.ritbb004 ? (
														<>{selectedRisk.ritbb004.length} {selectedRisk.ritbb004.length > 1 ? translate("incidentes") : translate("incidente")}</>
													): (
														<>{`0 ${translate("análises")}`}</>
													)} 
												</Title>
											</RCCGrid>
											<hr />

											{ListOfIncidents}	
										</>
									)
								}]}
						/>
					</Modal>
				</>
			)}
		</>
	);
}
;


export default forwardRef(ModalHistoric);
